/**
 * Lo que comparten todas las ventanas de Stripe incrustadas en el panel.
 *
 * Connect pide crear UNA instancia por sesión y reutilizarla: cada
 * `loadConnectAndInitialize` carga su script y abre su propia AccountSession.
 * Mientras sólo había una vista incrustada eso vivía dentro de ella; con cuatro
 * —el cobro, los avisos, los depósitos y los datos de la cuenta— dejaría de ser
 * un detalle suyo para convertirse en cuatro copias que se pisan.
 *
 * Los componentes que la sesión autoriza se fijan al crearla, en
 * `commerce.ts`. Aquí no se elige nada: se pide el secreto y se comparte la
 * instancia que salga.
 */
import type { Translate } from '@webbuilder/i18n'
import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { loadConnectAndInitialize, type StripeConnectInstance } from '@stripe/connect-js'
import { ConnectComponentsProvider } from '@stripe/react-connect-js'
import { createStripeAccountSession } from '../../../lib/api'
import { SpinningMascot } from '../../RecordLoader'
import { translate, useI18n } from '../../../lib/i18n'

/**
 * Sin clave publicable no hay nada que incrustar.
 *
 * Se pregunta antes de ofrecer la vista, no después de intentar abrirla: un
 * botón que lleva a un error es peor que un botón que no está.
 */
export function isEmbeddedStripeConfigured(): boolean {
  return Boolean(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY)
}

/** Una por proyecto: la sesión delega el acceso a UNA cuenta conectada. */
const instances = new Map<string, StripeConnectInstance>()

/**
 * Sobre qué se pinta el embed.
 *
 * `overlay` es la ventana que flota sobre el fondo oscurecido; `inline`, la que
 * va dentro de una página. No es un matiz: el color de fondo que hace que una
 * parezca elevada hace que la otra parezca un agujero recortado en la página.
 */
export type EmbedSurface = 'overlay' | 'inline'

/**
 * Los colores del panel, leídos del documento.
 *
 * El iframe hereda la tipografía y el fondo de su contenedor, pero lo demás hay
 * que dárselo explícitamente. Se leen las variables vivas en vez de escribir los
 * valores otra vez: así el tema oscuro no necesita una segunda tabla que
 * mantener en paralelo, y lo que cambie en la hoja de estilos llega aquí solo.
 */
function appearanceFromTheme(surface: EmbedSurface) {
  const styles = getComputedStyle(document.documentElement)
  const read = (name: string, fallback: string) => styles.getPropertyValue(name).trim() || fallback
  return {
    variables: {
      colorPrimary: read('--dash-accent', '#0f72f0'),
      colorText: read('--dash-text', '#111827'),
      /* Token propio y no `--dash-panel` (traslúcido en oscuro: se veía a través)
         ni `--dash-float-bg` (un punto más claro, y Stripe aclara aún más sus
         superficies internas a partir de esto: acababa gris medio). */
      colorBackground: surface === 'inline'
        ? read('--dash-embed-inline-bg', '#ffffff')
        : read('--dash-embed-bg', '#ffffff'),
      colorSecondaryText: read('--dash-muted', '#6b7280'),
      colorBorder: read('--dash-border', '#e5e7eb'),
      borderRadius: '12px',
    },
  }
}

/**
 * El proveedor de Connect, con su carga y su fallo ya resueltos.
 *
 * `fallback` es lo que se pinta mientras Stripe llega o cuando no llegó. Por
 * defecto no se pinta nada: el aviso de la cuenta es un componente que a veces
 * no tiene nada que decir, y un «cargando…» que se queda ahí para siempre sería
 * peor que el silencio. Las vistas que ocupan la pantalla sí pasan el suyo.
 */
export function StripeEmbedProvider({ projectId, token, children, fallback = null, onFail, surface = 'overlay' }: {
  projectId: string
  token: string
  children: ReactNode
  fallback?: ReactNode
  /** Se avisa hacia arriba para que la sección pueda esconder su marco entero. */
  onFail?: ((message: string) => void) | undefined
  /** Sobre qué se pinta. Decide el color de fondo; ver `EmbedSurface`. */
  surface?: EmbedSurface
}) {
  const [instance, setInstance] = useState<StripeConnectInstance | null>(null)
  /*
   * El idioma de las ventanas de Stripe, el mismo que el del panel.
   *
   * Iba fijo en `es-MX`: con el panel en inglés, abrir el detalle de un cobro
   * cambiaba de idioma a mitad de pantalla —el encabezado nuestro en inglés y
   * todo lo de dentro en castellano—. Stripe acepta el idioma al crear la
   * instancia y también después, con `update`, así que cambiar de idioma con una
   * ventana abierta la traduce sin volver a cargarla.
   *
   * `en-US` y no `en` a secas: Stripe elige con él el formato de fechas y
   * cantidades, y el mercado principal es Estados Unidos. En castellano, `es`
   * sin región, porque el idioma del panel no dice si quien lo usa está en
   * México o en España.
   */
  const { language } = useI18n()
  const stripeLocale = language === 'en' ? 'en-US' : 'es'
  const [failed, setFailed] = useState('')

  /* La biblioteca vuelve a llamar a esto cuando la sesión caduca, así que crea
     una nueva cada vez en vez de devolver la de antes guardada. */
  const fetchClientSecret = useCallback(
    async () => (await createStripeAccountSession(projectId, token)).clientSecret,
    [projectId, token],
  )

  /* La instancia se comparte, y la superficie no: quien la reutiliza le pasa la
     suya al montarse (el `update` de abajo), así que ir de la ficha de un cobro
     —que flota— a Depósitos —que no— repinta los colores en vez de heredar los
     de la vista anterior. */
  const appearance = useMemo(() => appearanceFromTheme(surface), [surface])

  const fail = useCallback((message: string) => {
    setFailed(message)
    onFail?.(message)
  }, [onFail])

  useEffect(() => {
    let alive = true
    const existing = instances.get(projectId)
    if (existing) {
      // Al reabrir ya está cargada; sólo se le pasan los colores y el idioma de
      // ahora, que pueden haber cambiado si entretanto se cambió el tema o el
      // idioma del panel.
      existing.update({ appearance, locale: stripeLocale })
      setInstance(existing)
      return
    }

    try {
      const created = loadConnectAndInitialize({
        publishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string,
        fetchClientSecret,
        appearance,
        locale: stripeLocale,
      })
      instances.set(projectId, created)
      if (alive) setInstance(created)
    } catch (error) {
      console.error('[stripe] no se pudo iniciar Stripe incrustado', error)
      if (alive) fail(translate('stripe.embedFailed'))
    }
    return () => { alive = false }
  }, [projectId, fetchClientSecret, appearance, fail, stripeLocale])

  if (failed) return <>{fallback}</>
  if (!instance) return <>{fallback}</>

  return (
    <ConnectComponentsProvider connectInstance={instance}>{children}</ConnectComponentsProvider>
  )
}

/**
 * El mensaje de un `onLoadError`, en algo que se pueda leer.
 *
 * `invalid_request_error` casi siempre es lo mismo y siempre es nuestro: la
 * cuenta de plataforma no tiene habilitados los componentes incrustados. Decirlo
 * ahorra el rato de buscarlo en el panel de Stripe.
 */
export function embedErrorMessage(
  error: { type?: string },
  /** La clave del sujeto, no el sujeto: «la disputa», «tus depósitos». */
  subjectKey: string,
  t: Translate,
): string {
  return error.type === 'invalid_request_error'
    ? t('stripe.embed.rejected')
    : t('stripe.embed.failed', { subject: t(subjectKey) })
}

/** El «cargando» de las vistas que ocupan sitio y tienen que decir que lo hacen. */
export function EmbedLoading({ label }: { label: string }) {
  /* La misma mascota que el resto de las esperas del panel. Aquí no hay
     despedida: quien avisa de que terminó es el propio marco de Stripe al
     aparecer, y no tenemos forma de saberlo con antelación para anunciarlo. */
  return (
    <div className="dash-placeholder dash-placeholder--inset dash-list-loading" role="status" aria-busy="true">
      <SpinningMascot spinning size={44} />
      <span>{label}</span>
    </div>
  )
}
